// ─────────────────────────────────────────────────────────────────────────────
// SSOT da TAXONOMIA (apresentação de domínio) — conteúdos e marketplace.
// Slugs = colunas reais do BD (marcianus_content.kind / marketplace.category).
// Antes vivia triplicado: apps/lean taxonomy.ts, apps/admin studio.ts (KIND_LABEL)
// e ContentPreview (TAXO). Agora há UMA fonte. Framework-agnóstico de propósito:
//   • `icon`     — NOME do ícone (a app lean mapeia p/ o seu <Icon/>).
//   • `colorVar` — token CSS (a app lean usa var()).
//   • `colorHex` — o MESMO valor resolvido (a consola HQ usa hex direto).
// ─────────────────────────────────────────────────────────────────────────────

export interface TaxoEntry { label: string; icon: string; colorVar: string; colorHex: string }
export interface TaxoMeta extends TaxoEntry { slug: string }

// ── Conteúdos ── valores = marcianus_content.kind
export const CONTENT_TAXO: Record<string, TaxoEntry> = {
  article:   { label: 'Artigo',      icon: 'edit',      colorVar: 'var(--ink-info)', colorHex: '#00D1FF' },
  report:    { label: 'Relatório',   icon: 'chart',     colorVar: 'var(--ink-danger)',    colorHex: '#FF3B5C' },
  news:      { label: 'Notícia',     icon: 'globe',     colorVar: 'var(--ink-warning)',   colorHex: '#FFB020' },
  blog:      { label: 'Blog',        icon: 'edit',      colorVar: 'var(--ink-accent)',    colorHex: '#7B61FF' },
  magazine:  { label: 'Revista',     icon: 'bookmark',  colorVar: 'var(--ink-taxo-pink)', colorHex: '#FF6B9D' },
  reportage: { label: 'Reportagem',  icon: 'camera',    colorVar: 'var(--ink-success)',   colorHex: '#00C48C' },
  podcast:   { label: 'Podcast',     icon: 'mic',       colorVar: 'var(--ink-success)',   colorHex: '#00C48C' },
  interview: { label: 'Entrevista',  icon: 'users',     colorVar: 'var(--purple-50)', colorHex: '#A78BFA' },
  curiosity: { label: 'Curiosidade', icon: 'sparkles',  colorVar: 'var(--money)',      colorHex: '#FFD700' },
  video:     { label: 'Vídeo',       icon: 'film',      colorVar: 'var(--ink-danger)',    colorHex: '#FF3B5C' },
  playbook:  { label: 'Playbook',    icon: 'target',    colorVar: 'var(--purple-50)', colorHex: '#A78BFA' },
  case:      { label: 'Caso',        icon: 'briefcase', colorVar: 'var(--ink-success)',   colorHex: '#00C48C' },
  media:     { label: 'Multimédia',  icon: 'film',      colorVar: 'var(--ink-info)', colorHex: '#00D1FF' },
};
export const CONTENT_DEFAULT: TaxoEntry = { label: 'Artigo', icon: 'edit', colorVar: 'var(--ink-info)', colorHex: '#00D1FF' };

// Ordem dos chips de filtro (só os tipos "de topo").
export const CONTENT_KIND_ORDER = [
  'article', 'report', 'news', 'blog', 'magazine', 'reportage',
  'podcast', 'interview', 'video', 'curiosity',
] as const;

// Todos os tipos aceites na criação/publicação (inclui os de compatibilidade).
export const CONTENT_KINDS = [
  'article', 'report', 'news', 'blog', 'magazine', 'reportage',
  'podcast', 'interview', 'video', 'curiosity', 'case', 'playbook',
] as const;

export function contentTaxo(kind?: string | null): TaxoMeta {
  const slug = (kind || '').toLowerCase();
  return { slug: slug || 'article', ...(CONTENT_TAXO[slug] ?? CONTENT_DEFAULT) };
}
export const kindLabel = (kind?: string | null) => contentTaxo(kind).label;

// Conteúdo audiovisual → "Assistir"; texto → "Ler".
export const WATCH_KINDS = new Set(['podcast', 'video', 'media', 'interview', 'reportage']);
export const contentCtaLabel = (kind?: string | null) =>
  (WATCH_KINDS.has((kind || '').toLowerCase()) ? 'Assistir' : 'Ler');

// ── Marketplace ── valores = marcianus_marketplace.category
export const MARKET_TAXO: Record<string, TaxoEntry> = {
  SaaS:         { label: 'SaaS',         icon: 'grid',     colorVar: 'var(--ink-info)',   colorHex: '#00D1FF' },
  HaaS:         { label: 'HaaS',         icon: 'cpu',      colorVar: 'var(--ink-taxo-orange)', colorHex: '#FF6B35' },
  DaaS:         { label: 'DaaS',         icon: 'activity', colorVar: 'var(--ink-accent)',      colorHex: '#7B61FF' },
  Infoprodutos: { label: 'Infoprodutos', icon: 'bookmark', colorVar: 'var(--money)',        colorHex: '#FFD700' },
  Serviços:     { label: 'Serviços',     icon: 'sparkles', colorVar: 'var(--ink-success)',     colorHex: '#00C48C' },
  Consultoria:  { label: 'Consultoria',  icon: 'users',    colorVar: 'var(--purple-50)',   colorHex: '#A78BFA' },
  Cursos:       { label: 'Cursos',       icon: 'award',    colorVar: 'var(--ink-info)',   colorHex: '#00D1FF' },
  Treinamentos: { label: 'Treinamentos', icon: 'target',   colorVar: 'var(--ink-danger)',      colorHex: '#FF3B5C' },
  Gestão:       { label: 'Gestão',       icon: 'settings', colorVar: 'var(--ink-warning)',     colorHex: '#FFB020' },
  Logística:    { label: 'Logística',    icon: 'refresh',  colorVar: 'var(--ink-success)',     colorHex: '#00C48C' },
  Catering:     { label: 'Catering',     icon: 'gift',     colorVar: 'var(--ink-taxo-pink)',   colorHex: '#FF6B9D' },
  Transporte:   { label: 'Transporte',   icon: 'mapPin',   colorVar: 'var(--ink-info)',   colorHex: '#00D1FF' },
  Hotelaria:    { label: 'Hotelaria',    icon: 'home',     colorVar: 'var(--purple-50)',   colorHex: '#A78BFA' },
  Outros:       { label: 'Outros',       icon: 'tag',      colorVar: 'var(--ink-taxo-steel)',  colorHex: '#8A8F98' },
};
export const MARKET_DEFAULT: TaxoEntry = { label: 'Serviços', icon: 'shop', colorVar: 'var(--ink-taxo-orange)', colorHex: '#FF6B35' };

export const MARKET_CATEGORY_ORDER = [
  'SaaS', 'HaaS', 'DaaS', 'Infoprodutos', 'Serviços', 'Consultoria',
  'Cursos', 'Treinamentos', 'Gestão', 'Logística', 'Catering', 'Transporte', 'Hotelaria',
] as const;

export function marketTaxo(category?: string | null): TaxoMeta {
  const slug = category || '';
  return { slug: slug || 'Outros', ...(MARKET_TAXO[slug] ?? MARKET_DEFAULT) };
}
